/** @jsxImportSource @emotion/react */
import { EuiIcon, EuiSpacer, useEuiTheme } from "@inscopix/ideas-eui";
import { ButtonDeleteTables } from "components/ProjectTablesTable/ButtonDeleteTables";
import { ProjectTablesSearchAndFilter } from "components/ProjectTablesTable/ProjectTablesSearchAndFilter";
import { ProjectTablesTable } from "components/ProjectTablesTable/ProjectTablesTable";
import { ButtonCreateTable } from "pages/gdt/layout/ButtonCreateTable";
import { ButtonMoveTables } from "components/ProjectTablesTable/ButtonMoveFolder";
import { useProjectTablesTableContext } from "./provider/ProjectTablesTableProvider";
import { shallow } from "zustand/shallow";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { ProjectTablesModals } from "./ProjectTablesModals";
import { css } from "@emotion/react";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { ButtonEmptyPermissioned } from "components/ButtonEmptyPermissioned/ButtonEmptyPermissioned";
import { uuid } from "utils/uuid";
import { ProjectTablesDraggableData } from "./ProjectTablesDndProvider";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { useState } from "react";
import { ModalCreateWorkflow } from "components/WorkflowEditor/ModalCreateWorkflow";

export const PROJECT_TABLES_REMOVE_FROM_FOLDER_DROPPABLE_ID = uuid();

export const ProjectTablesTableLayout = () => {
  const [showModalWorkflow, setShowModalWorkflow] = useState(false);
  const anyVisibleTableIsSelected = useProjectTablesTableContext(
    (s) => s.getAnyVisibleTableIsSelected(),
    shallow,
  );

  const tables = useProjectTablesTableContext((s) => s.tables);
  const canEdit = useProjectPermissionStore((s) => s.permissions.edit);

  const { active } = useDndContext();

  const theme = useEuiTheme();

  const { setNodeRef, isOver } = useDroppable({
    id: PROJECT_TABLES_REMOVE_FROM_FOLDER_DROPPABLE_ID,
  });

  const isActiveInFolder = (() => {
    const activeTableId = (
      active?.data.current as ProjectTablesDraggableData | undefined
    )?.tableId;
    return tables.some(
      (table) => table.id === activeTableId && table.folder !== null,
    );
  })();

  const selectedTableIds = useProjectTablesTableContext((s) =>
    Object.entries(s.selectedTableMap).filter(([_, isSelected]) => isSelected),
  ).map(([id]) => id);

  const selectedAnalysisTableIds = useDataTableContext((s) =>
    s.tables
      .filter((table) => table.kind === "analysis")
      .filter(({ id: analysisTableId }) =>
        selectedTableIds.some((id) => id === analysisTableId),
      )
      .map(({ id }) => id),
  );

  return (
    <>
      <ProjectTablesSearchAndFilter />
      <EuiSpacer size="m" />
      <ProjectTablesTable />
      <EuiSpacer size="m" />
      {canEdit && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          {active !== null && (
            <ButtonEmptyPermissioned
              buttonRef={setNodeRef}
              requiredPermission="edit"
              css={css`
                border: ${isOver && isActiveInFolder ? "2px" : "1px"} dashed
                  ${theme.euiTheme.colors.lightShade};
                border-radius: 5px;
                width: 100%;
                color: ${isActiveInFolder
                  ? theme.euiTheme.colors.text
                  : theme.euiTheme.colors.lightShade};
              `}
            >
              <EuiIcon type="returnKey" /> Remove from folder
            </ButtonEmptyPermissioned>
          )}
          {!anyVisibleTableIsSelected && active === null && (
            <ButtonCreateTable />
          )}
          {anyVisibleTableIsSelected && active === null && (
            <>
              {selectedAnalysisTableIds.length > 1 && (
                <ButtonEmptyPermissioned
                  requiredPermission="edit"
                  iconType="timelineWithArrow"
                  color="primary"
                  onClick={() => setShowModalWorkflow(true)}
                >
                  Create Workflow
                </ButtonEmptyPermissioned>
              )}
              <ButtonMoveTables />
              <EuiSpacer />
              <ButtonDeleteTables />
            </>
          )}
        </div>
      )}
      <ProjectTablesModals />
      {showModalWorkflow && (
        <ModalCreateWorkflow
          analysisTableIds={selectedAnalysisTableIds}
          onClose={() => setShowModalWorkflow(false)}
        />
      )}
    </>
  );
};
